import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Switch,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  Grid,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonalIntegrations from "../../components/integrations/PersonalIntegrations";
import { connectors } from "../constants/connectors";
import { updateConnectorSharing } from "../../services/connectors.service";

export default function ConnectorsTabPanel(props: any) {
  const {
    connectorTabValue,
    setConnectorTabValue,
    connectorAuthorization,
    handleCardClick,
    handleToggle,
    selectedOrganization,
    selectedOrgObj,
    organisationList = [],
    refreshConnectors,
    login,
  } = props;
  const [sharingConnector, setSharingConnector] = useState<any>(null);
  const [sharedChildIds, setSharedChildIds] = useState<string[]>([]);
  const [savingSharing, setSavingSharing] = useState(false);

  const childOrganisations = organisationList.filter(
    (org: any) => String(org.parentOrganisationId || "") === String(selectedOrganization),
  );

  const openSharing = (connectorData: any, connectorDefinition: any) => {
    setSharingConnector({ ...connectorDefinition, ...connectorData });
    setSharedChildIds(connectorData?.sharedWithOrganisationIds || []);
  };

  const saveSharing = async () => {
    if (!sharingConnector?._id) return;
    setSavingSharing(true);
    try {
      await updateConnectorSharing(sharingConnector._id, sharedChildIds);
      setSharingConnector(null);
      await refreshConnectors?.();
    } finally {
      setSavingSharing(false);
    }
  };

  return (
    <Box id="connectors-tab">
      {/* SUB-TABS FOR ORGANISATION VS PERSONAL */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={connectorTabValue}
          onChange={(_e, val) => setConnectorTabValue(val)}
          aria-label="connector tabs"
        >
          <Tab label="Organisation Integrations" />
          <Tab label="Personal Integrations" />
        </Tabs>
      </Box>

      {/* TAB 0: ORGANISATION INTEGRATIONS */}
      {connectorTabValue === 0 && (
        <>
          {selectedOrganization === "All Organisation" &&
          login?.data?.role === "global admin" ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 5,
                textAlign: "center",
                height: "60vh",
                gap: 2,
              }}
            >
              <img
                src="/illustrations/NoConnector.png"
                alt="Select an organisation"
                style={{ width: "180px", opacity: 0.7 }}
              />
              <Typography variant="h5" fontWeight={600} color="text.primary">
                Select an Organisation
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 380 }}
              >
                Choose a specific organisation from the dropdown above to view
                and manage its integrations.
              </Typography>
            </Box>
          ) : (
            <Box>
              {(() => {
                // 1. USE HARDCODED CONFIG (Don't merge with backend)
                const processedConnectors = connectors.map((conn) => {
                  const key = conn.name
                    .replace(/ /g, "_")
                    .replace(/\./g, "")
                    .toLowerCase();
                  const dynamicData = connectorAuthorization[key];

                  return {
                    ...conn,
                    displayCategory: conn.displayCategory,
                    capabilities: conn.capabilities,
                    status: dynamicData?.status || false,
                  };
                });

                // 2. GROUP by Category
                const groupedConnectors = processedConnectors.reduce(
                  (acc: any, conn) => {
                    const category = conn.displayCategory;
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(conn);
                    return acc;
                  },
                  {},
                );

                // 3. SORT Categories (Most connectors first)
                const sortedCategories = Object.keys(groupedConnectors).sort(
                  (a, b) =>
                    groupedConnectors[b].length - groupedConnectors[a].length,
                );

                // 4. RENDER Loop
                return sortedCategories.map((category) => (
                  <Box key={category} mb={4}>
                    {/* Category Header */}
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        fontSize: "0.85rem",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {category}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={3}>
                      {groupedConnectors[category].map((conn: any) => (
                        <Grid
                          item
                          key={conn.id}
                          id={`connector-card-${conn.id}`}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                        >
                          {(() => {
                            const connKey = conn.name
                              .replace(/ /g, "_")
                              .replace(/\./g, "")
                              .toLowerCase();
                            const connectorData = connectorAuthorization[connKey];
                            const isConnected = !!connectorData?._id;
                            // For connectors with multiple legitimate connection
                            // methods (currently Microsoft 365: one-click OAuth OR
                            // legacy manually-entered credentials), a Connector doc
                            // existing and its `status` flag being true is not proof
                            // any of them actually completed — only a provider-set
                            // proof field is. Connectors with no connectionProofKeys
                            // have no extra method to verify, so this is always true.
                            const hasConnectionProof = conn.connectionProofKeys
                              ? conn.connectionProofKeys.some(
                                  (key: string) => !!connectorData?.[key],
                                )
                              : true;
                            // Saved config that nothing has ever verified must not
                            // read as Connected (CONN-02). Strictly `=== false`:
                            // an absent flag means the connector predates this
                            // tracking and stays grandfathered as before.
                            const credentialsUnverified =
                              connectorData?.metadata?.credentialsVerified === false;
                            const isFullyConnected =
                              isConnected && hasConnectionProof && !credentialsUnverified;
                            const isEnabled = connectorData?.status || false;
                            const isOAuth = conn.authType === "oauth";
                            const isInherited = Boolean(connectorData?.inherited);
                            return (
                              <Card
                                sx={{
                                  borderRadius: "12px",
                                  border: "1.5px solid",
                                  borderColor: "#E5E7EB",
                                  boxShadow: "none",
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  transition:
                                    "border-color 0.15s, box-shadow 0.15s",
                                  "&:hover": {
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                                    borderColor: "primary.main",
                                  },
                                }}
                              >
                                {/* Icon Area */}
                                <Box
                                  sx={{
                                    background: conn.bgColor,
                                    p: 2.5,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    minHeight: 80,
                                    borderRadius: "10px 10px 0 0",
                                  }}
                                >
                                  {typeof conn.icon === "string" ? (
                                    <img
                                      src={`${conn.icon}`}
                                      alt={conn.name}
                                      style={{
                                        width: 36,
                                        height: 36,
                                        objectFit: "contain",
                                      }}
                                    />
                                  ) : (
                                    <conn.icon
                                      sx={{ fontSize: 36, color: "white" }}
                                    />
                                  )}
                                </Box>

                                {/* Content Area */}
                                <CardContent
                                  sx={{
                                    flexGrow: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    p: 2,
                                  }}
                                >
                                  {/* Header + status badge */}
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    mb={0.75}
                                  >
                                    <Typography
                                      fontWeight={600}
                                      fontSize="0.9375rem"
                                      color="text.primary"
                                    >
                                      {conn.name}
                                    </Typography>
                                    {isInherited ? (
                                      <Chip
                                        label="Shared by parent"
                                        size="small"
                                        color="info"
                                      />
                                    ) : isFullyConnected && isEnabled ? (
                                      <Chip
                                        icon={
                                          <CheckCircleIcon
                                            sx={{
                                              fontSize: "14px !important",
                                            }}
                                          />
                                        }
                                        label="Connected"
                                        size="small"
                                        sx={{
                                          fontSize: "0.6875rem",
                                          height: 22,
                                          bgcolor: "#D1FAE5",
                                          color: "#065F46",
                                          fontWeight: 600,
                                          "& .MuiChip-icon": {
                                            color: "#059669",
                                          },
                                        }}
                                      />
                                    ) : isFullyConnected ? (
                                      <Chip
                                        label="Disabled"
                                        size="small"
                                        sx={{
                                          fontSize: "0.6875rem",
                                          height: 22,
                                          bgcolor: "#FEF3C7",
                                          color: "#92400E",
                                          fontWeight: 500,
                                        }}
                                      />
                                    ) : credentialsUnverified ? (
                                      // Credentials are stored but unproven —
                                      // say so instead of a bare "Not connected",
                                      // which reads as "nothing was saved".
                                      <Chip
                                        label="Not verified"
                                        size="small"
                                        title={`${conn.name} settings are saved, but the connection has not been verified. Finish authorising ${conn.name} to connect.`}
                                        sx={{
                                          fontSize: "0.6875rem",
                                          height: 22,
                                          bgcolor: "#FEF3C7",
                                          color: "#92400E",
                                          fontWeight: 500,
                                        }}
                                      />
                                    ) : (
                                      <Chip
                                        label="Not connected"
                                        size="small"
                                        sx={{
                                          fontSize: "0.6875rem",
                                          height: 22,
                                          bgcolor: "#F3F4F6",
                                          color: "#6B7280",
                                          fontWeight: 500,
                                        }}
                                      />
                                    )}
                                  </Box>

                                  {/* Description */}
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      flexGrow: 1,
                                      lineHeight: 1.6,
                                      mb: 1.5,
                                      fontSize: "0.8125rem",
                                    }}
                                  >
                                    {conn.description}
                                  </Typography>

                                  {/* Capabilities */}
                                  <Box
                                    display="flex"
                                    gap={0.75}
                                    flexWrap="wrap"
                                    mb={1.5}
                                  >
                                    {conn.capabilities?.map((cap: string) => (
                                      <Chip
                                        key={cap}
                                        label={cap}
                                        size="small"
                                        sx={{
                                          fontSize: "0.625rem",
                                          height: 18,
                                          bgcolor: "#EFF6FF",
                                          color: "#1D4ED8",
                                          fontWeight: 600,
                                          borderRadius: "4px",
                                        }}
                                      />
                                    ))}
                                  </Box>

                                  {/* Action row */}
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    mt="auto"
                                  >
                                    {isFullyConnected ? (
                                      <>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={() => handleCardClick(conn)}
                                          disabled={isOAuth || isInherited}
                                          sx={{
                                            fontSize: "0.8125rem",
                                            px: 1.5,
                                          }}
                                        >
                                          Manage
                                        </Button>
                                        <Switch
                                          checked={isEnabled}
                                          onChange={(e) =>
                                            handleToggle(e, conn)
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                          disabled={!conn.enabled || isInherited}
                                          size="small"
                                        />
                                      </>
                                    ) : (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        fullWidth
                                        onClick={() => handleCardClick(conn)}
                                        disabled={!conn.enabled}
                                        sx={{ fontSize: "0.8125rem" }}
                                      >
                                        Set up
                                      </Button>
                                    )}
                                  </Box>
                                  {isFullyConnected &&
                                    !isInherited &&
                                    selectedOrgObj?.hierarchyRole === "parent" &&
                                    childOrganisations.length > 0 && (
                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => openSharing(connectorData, conn)}
                                        sx={{ mt: 1 }}
                                      >
                                        Share with child tenants
                                      </Button>
                                    )}
                                </CardContent>
                              </Card>
                            );
                          })()}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ));
              })()}
            </Box>
          )}
        </>
      )}

      {/* TAB 1: PERSONAL INTEGRATIONS */}
      {connectorTabValue === 1 && <PersonalIntegrations />}

      <Dialog open={Boolean(sharingConnector)} onClose={() => setSharingConnector(null)} fullWidth maxWidth="sm">
        <DialogTitle>Share {sharingConnector?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Only selected direct child organisations can use this connector. Child-owned connectors take priority.
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="connector-child-sharing-label">Child organisations</InputLabel>
            <Select
              labelId="connector-child-sharing-label"
              multiple
              label="Child organisations"
              value={sharedChildIds}
              onChange={(event) => setSharedChildIds(event.target.value as string[])}
            >
              {childOrganisations.map((org: any) => (
                <MenuItem key={org._id} value={org._id}>{org.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSharingConnector(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveSharing} disabled={savingSharing}>
            {savingSharing ? "Saving…" : "Save sharing"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
